import type {
    ApplicationDocument,
    DocumentStatus,
    DocumentType,
} from '@api/document';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { BankDetailsNextAction } from '@src/api/bankDetails';

type VerifyDecision = 'SUBMITTED' | 'RESUBMISSION' | 'APPROVED' | 'DECLINED';
type DecisionStatus = 'pending' | 'approved' | 'declined' | 'existing-loan';

export type CurrentStep =
    | 'bank_details'
    | 'verify'
    | 'documents'
    | 'submit'
    | 'approved'
    | 'declined';

type BankDetails = {
    phoneNumber: string;
    accountHolderName: string;
    sortCode: string;
    accountNumber: string;
    isUkPersonalAccountAndAuthorisedForDD: boolean;
};

export type PerAppData = {
    currentStep: CurrentStep | null;
    bankDetails: BankDetails;
    verify: {
        decision: VerifyDecision | null;
        veriffSessionId: string | null;
        verificationUrl: string | null;
    };
    docs: ApplicationDocument[];
    decisionStatus: DecisionStatus;
    bankDetailsComplete: boolean;
    bankDetailsNextAction: BankDetailsNextAction | null;
    verifyComplete: boolean;
    docsComplete: boolean;
};

const defaultBankDetails: BankDetails = {
    phoneNumber: '',
    accountHolderName: '',
    sortCode: '',
    accountNumber: '',
    isUkPersonalAccountAndAuthorisedForDD: false,
};

export const defaultPerAppData: PerAppData = {
    currentStep: null,
    bankDetails: defaultBankDetails,
    verify: {
        decision: null,
        veriffSessionId: null,
        verificationUrl: null,
    },
    docs: [],
    decisionStatus: 'pending',
    bankDetailsComplete: false,
    bankDetailsNextAction: null,
    verifyComplete: false,
    docsComplete: false,
};

export type ApplicationDataByAppId = Record<string, PerAppData>;

interface ApplicationState {
    currentAppId: string | null;
    applicationDataByAppId: ApplicationDataByAppId;
    setCurrentAppId: (appId: string | null) => void;
    setCurrentStep: (step: CurrentStep | null) => void;
    setBankDetails: (details: Partial<BankDetails>) => void;
    setBankDetailsComplete: (complete: boolean) => void;
    setBankDetailsNextAction: (
        nextAction: BankDetailsNextAction | null,
    ) => void;
    setVerifyDecision: (decision: VerifyDecision | null) => void;
    setVerificationData: (data: {
        veriffSessionId: string | null;
        verificationUrl: string | null;
    }) => void;
    setDocs: (docs: ApplicationDocument[]) => void;
    setDocsComplete: (complete: boolean) => void;
    setDecisionStatus: (status: DecisionStatus) => void;
    updateDocStatus: (type: DocumentType, status: DocumentStatus) => void;
    reset: () => void;
}

/**
 * Returns the application data for the currentAppId, or defaults if none.
 * Use in selectors: useApplicationStore(s => getCurrentAppData(s).bankDetails)
 */
export function getCurrentAppData(state: {
    currentAppId: string | null;
    applicationDataByAppId: ApplicationDataByAppId;
}): PerAppData {
    if (!state.currentAppId) return defaultPerAppData;
    return (
        state.applicationDataByAppId[state.currentAppId] ?? defaultPerAppData
    );
}

function updateCurrentApp(
    state: ApplicationState,
    updater: (current: PerAppData) => PerAppData,
): Partial<ApplicationState> {
    if (!state.currentAppId) return {};
    const current = getCurrentAppData(state);
    return {
        applicationDataByAppId: {
            ...state.applicationDataByAppId,
            [state.currentAppId]: updater(current),
        },
    };
}

export const useApplicationStore = create<ApplicationState>()(
    persist(
        (set) => ({
            currentAppId: null,
            applicationDataByAppId: {},
            setCurrentAppId: (appId) =>
                set((_state) => {
                    return { currentAppId: appId };
                }),
            setCurrentStep: (step) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        currentStep: step,
                    })),
                ),
            setBankDetails: (details) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        bankDetails: { ...current.bankDetails, ...details },
                    })),
                ),
            setBankDetailsComplete: (complete) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        bankDetailsComplete: complete,
                    })),
                ),
            setBankDetailsNextAction: (nextAction) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        bankDetailsNextAction: nextAction,
                    })),
                ),
            setVerifyDecision: (decision) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        verify: { ...current.verify, decision },
                        verifyComplete: decision === 'APPROVED',
                    })),
                ),
            setVerificationData: (data) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        verify: {
                            ...current.verify,
                            veriffSessionId: data.veriffSessionId,
                            verificationUrl: data.verificationUrl,
                        },
                    })),
                ),
            setDocs: (docs) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        docs,
                    })),
                ),
            updateDocStatus: (type: DocumentType, status: DocumentStatus) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        docs: current.docs.map((doc) =>
                            doc.type === type ? { ...doc, status } : doc,
                        ),
                    })),
                ),
            setDecisionStatus: (status) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        decisionStatus: status,
                    })),
                ),
            setDocsComplete: (docsComplete) =>
                set((state) =>
                    updateCurrentApp(state, (current) => ({
                        ...current,
                        docsComplete,
                    })),
                ),
            reset: () =>
                set((state) =>
                    state.currentAppId
                        ? {
                              applicationDataByAppId: {
                                  ...state.applicationDataByAppId,
                                  [state.currentAppId]: defaultPerAppData,
                              },
                          }
                        : {},
                ),
        }),
        {
            name: 'application-store',
            partialize: (state) => ({
                currentAppId: state.currentAppId,
                applicationDataByAppId: state.applicationDataByAppId,
            }),
            version: 1,
        },
    ),
);
